import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getMovieById } from "@/lib/catalog";
import { heroSlides } from "@/mocks/heroes";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const timer = useRef<number | null>(null);
  const paused = useRef(false);
  const touchStartX = useRef<number | null>(null);

  const slides = heroSlides.flatMap((slide) => {
    const movie = getMovieById(slide.id);
    return movie ? [{ ...slide, movie }] : [];
  });

  const total = slides.length;

  useEffect(() => {
    const tick = () => {
      if (!paused.current) setIndex((current) => (current + 1) % total);
    };
    timer.current = window.setInterval(tick, 6000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [total]);

  const go = (next: number) => setIndex((next + total) % total);
  const active = slides[index] || slides[0];

  if (!active) return null;

  return (
    <section
      aria-label="焦点影片轮播"
      className="relative w-full overflow-hidden bg-background-100"
      onMouseEnter={() => {
        paused.current = true;
      }}
      onMouseLeave={() => {
        paused.current = false;
      }}
      onTouchStart={(event) => {
        paused.current = true;
        touchStartX.current = event.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(event) => {
        const startX = touchStartX.current;
        const endX = event.changedTouches[0]?.clientX ?? null;
        if (startX !== null && endX !== null) {
          const delta = endX - startX;
          if (Math.abs(delta) > 50) go(delta < 0 ? index + 1 : index - 1);
        }
        touchStartX.current = null;
        paused.current = false;
      }}
    >
      <div className="relative h-[560px] w-full md:h-[660px] lg:h-[700px]">
        {slides.map((slide, slideIndex) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-[900ms] ${
              slideIndex === index ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden={slideIndex !== index}
          >
            <img
              src={slide.image}
              alt={`${slide.movie.title} 焦点剧照`}
              title={`${slide.movie.title} - 日本情色电影焦点推荐`}
              className={`h-full w-full object-cover object-top ${slideIndex === index ? "animate-slow-zoom" : ""}`}
            />
            <div className="absolute inset-0 hero-mask" />
          </div>
        ))}

        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto w-full max-w-[1400px] px-4 pb-10 md:px-8 md:pb-14">
            <div key={active.id} className="animate-rise max-w-2xl">
              <p className="flex items-center gap-3 text-[11px] tracking-widest2 text-accent-400 font-label">
                <span className="h-px w-8 bg-accent-500" />
                {active.kicker}
              </p>
              <h2 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-50 md:text-5xl">
                {active.movie.title}
              </h2>
              <p className="mt-2 font-display text-lg italic text-foreground-400 md:text-xl">{active.movie.titleJa}</p>

              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] text-foreground-300 font-label">
                <span className="flex items-center gap-1 text-accent-400">
                  <i className="ri-star-fill text-[11px]" />
                  {active.movie.rating.toFixed(1)}
                </span>
                <span>{active.movie.year}</span>
                <span className="text-foreground-600">/</span>
                <span>{active.movie.region}</span>
                <span className="text-foreground-600">/</span>
                <span>{active.movie.runtime} 分钟</span>
                <span className="text-foreground-600">/</span>
                <span>导演 {active.movie.director}</span>
              </div>

              <p className="mt-4 max-w-xl text-[13px] leading-relaxed text-foreground-300 md:text-sm">
                {active.movie.synopsis}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${active.movie.id}`}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-[13px] font-medium text-foreground-50 transition-colors hover:bg-primary-600 font-label"
                >
                  查看影片档案
                  <i className="ri-arrow-right-line text-sm" />
                </Link>
                <Link
                  to="/category/new"
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-foreground-300/40 px-5 py-2.5 text-[13px] text-foreground-100 transition-colors hover:border-accent-500 hover:text-accent-400 font-label"
                >
                  浏览新片单元
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute right-4 top-1/2 z-10 hidden -translate-y-1/2 flex-col gap-2 md:right-8 lg:flex">
          {slides.map((slide, slideIndex) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => go(slideIndex)}
              className={`group flex cursor-pointer items-center justify-end gap-3 text-right transition-colors ${
                slideIndex === index ? "text-accent-400" : "text-foreground-500 hover:text-foreground-200"
              }`}
            >
              <span className="max-w-0 overflow-hidden whitespace-nowrap text-[11px] opacity-0 transition-all duration-300 group-hover:max-w-[140px] group-hover:opacity-100 font-label">
                {slide.movie.title}
              </span>
              <span className="font-display text-sm">{String(slideIndex + 1).padStart(2, "0")}</span>
              <span
                className={`h-px transition-all duration-300 ${
                  slideIndex === index ? "w-10 bg-accent-500" : "w-5 bg-background-500 group-hover:bg-foreground-400"
                }`}
              />
            </button>
          ))}
        </div>

        <div className="absolute bottom-4 right-4 flex items-center gap-2 md:right-8">
          <button
            type="button"
            onClick={() => go(index - 1)}
            aria-label="上一张"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-foreground-300/30 bg-background-50/40 text-foreground-100 backdrop-blur transition-colors hover:border-accent-500 hover:text-accent-400"
          >
            <i className="ri-arrow-left-s-line" />
          </button>
          <button
            type="button"
            onClick={() => go(index + 1)}
            aria-label="下一张"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-foreground-300/30 bg-background-50/40 text-foreground-100 backdrop-blur transition-colors hover:border-accent-500 hover:text-accent-400"
          >
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>

        <div className="absolute bottom-4 left-4 flex items-center gap-1.5 md:left-8">
          {slides.map((slide, slideIndex) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => go(slideIndex)}
              aria-label={`切换到 ${slide.movie.title}`}
              className={`h-1 cursor-pointer transition-all duration-300 ${
                slideIndex === index ? "w-8 bg-accent-500" : "w-4 bg-foreground-300/40 hover:bg-foreground-300/70"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}